/**
 * เปลี่ยนรหัสผ่านของตัวเอง
 *
 * ลำดับจริง (ยืนยันรหัสเดิม → เปลี่ยน → เตะ session อื่น) อยู่ใน changePassword
 * ที่ config/supabase.ts — ฟอร์มนี้ไม่รู้จัก Supabase
 *
 * @module components/settings/ProfilePasswordForm
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../Input";
import { Button } from "../Button";
import { changePassword } from "@/config/supabase";
import { MIN_PASSWORD_LENGTH } from "@/config/auth";

export interface ProfilePasswordFormProps {
  disabled: boolean;
  disabledReason?: string;
}

export const ProfilePasswordForm: React.FC<ProfilePasswordFormProps> = ({
  disabled,
  disabledReason,
}) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const valid =
    current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && confirm === next;

  const handleSubmit = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    const result = await changePassword(current, next, t);
    setBusy(false);

    if (!result.success) {
      // ไม่ล้างช่องใดๆ — ให้ผู้ใช้แก้ไขช่องใดช่องหนึ่งและส่งใหม่ได้โดยไม่ต้องพิมพ์ซ้ำ
      setError(result.error ?? t("settings.couldNotChangePassword"));
      return;
    }

    // สำเร็จ — ล้างทุกช่อง ไม่มีเหตุผลให้รหัสค้างอยู่บนจอ
    setCurrent("");
    setNext("");
    setConfirm("");
    // service คืน success:true พร้อม error ได้ เมื่อรหัสเปลี่ยนแล้วแต่เตะเครื่องอื่นไม่สำเร็จ
    setNotice(result.error ?? t("settings.passwordChangedSignedOut"));
  };

  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
      <h2 className="text-base font-semibold text-ink-900">{t('settings.password')}</h2>

      {disabledReason && <p className="text-sm text-neutral-700">{disabledReason}</p>}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2 text-sm">
          {error}
        </div>
      )}
      {notice && !error && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-md px-3 py-2 text-sm">
          {notice}
        </div>
      )}

      <div className="space-y-4">
        <Input
          label={t('settings.currentPassword')}
          type="password"
          autoComplete="current-password"
          value={current}
          disabled={disabled || busy}
          onChange={(e) => {
            setCurrent(e.target.value);
            setError(null);
            setNotice(null);
          }}
        />
        <Input
          label={t('settings.newPassword')}
          type="password"
          autoComplete="new-password"
          value={next}
          disabled={disabled || busy}
          hint={t('settings.passwordHint', { n: MIN_PASSWORD_LENGTH })}
          onChange={(e) => {
            setNext(e.target.value);
            setError(null);
            setNotice(null);
          }}
        />
        <Input
          label={t('settings.confirmNewPassword')}
          type="password"
          autoComplete="new-password"
          value={confirm}
          disabled={disabled || busy}
          onChange={(e) => {
            setConfirm(e.target.value);
            setError(null);
            setNotice(null);
          }}
        />
      </div>

      {/* ก่อนปุ่ม ไม่ใช่หลังกด — คนควรรู้ผลก่อนตัดสินใจ */}
      <p className="text-sm text-neutral-700">
        {t('settings.passwordSignOutNote')}
      </p>

      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={disabled || busy || !valid}
          loading={busy}
        >
          {t('settings.changePassword')}
        </Button>
      </div>
    </section>
  );
};

export default ProfilePasswordForm;
